import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const services = [
  {
    title: 'General Liability & Property',
    icon: 'ri-shield-check-line',
    image: 'https://public.readdy.ai/ai/img_res/eff7b04a1ffcd9f860fc12a753ac65ef.jpg',
    desc: 'Protect your business from third-party claims and property damage with essential coverage that fits your budget.',
    items: ['General Liability', 'Commercial Property', 'Products & Completed Ops', 'Premises Liability', 'Fire & Theft Coverage', 'Equipment Coverage'],
  },
  {
    title: 'Workers Compensation',
    icon: 'ri-user-heart-line',
    image: 'https://readdy.ai/api/search-image?query=industrial%20workers%20safety%20equipment%20Appalachian%20coal%20mine%20operation%20professional%20workers%20protective%20gear%20dramatic%20industrial%20photography%20wide%20angle&width=700&height=460&seq=svc2026b&orientation=landscape',
    desc: 'Cover your employees for on-the-job injuries and illnesses. Required in most states and critical for any business with employees.',
    items: ['Medical Expense Coverage', 'Lost Wage Replacement', 'Employer\'s Liability', 'Return-to-Work Programs', 'State Compliance', 'High-Risk Industry Rates'],
  },
  {
    title: 'Commercial Auto',
    icon: 'ri-truck-line',
    image: 'https://readdy.ai/api/search-image?query=commercial%20trucks%20fleet%20vehicles%20on%20Appalachian%20highway%20mountain%20road%20professional%20transportation%20photography%20dramatic%20wide%20angle%20industrial%20tones&width=700&height=460&seq=svc2026c&orientation=landscape',
    desc: 'Fleet and commercial vehicle coverage for contractors, tradesmen, and businesses that depend on their vehicles.',
    items: ['Liability Coverage', 'Collision & Comprehensive', 'Uninsured Motorist', 'Hired & Non-Owned Auto', 'Fleet Programs', 'Cargo Coverage'],
  },
  {
    title: 'Business Owner\'s Policy (BOP)',
    icon: 'ri-building-2-line',
    image: 'https://readdy.ai/api/search-image?query=small%20business%20storefront%20Appalachian%20mountain%20town%20main%20street%20professional%20exterior%20warm%20lighting%20clear%20sky%20architectural%20photography%20neutral%20tones&width=700&height=460&seq=svc2026d&orientation=landscape',
    desc: 'Combines general liability and commercial property into one easy-to-manage, cost-effective policy for small businesses.',
    items: ['Liability + Property Bundle', 'Business Interruption', 'Equipment Breakdown', 'Data Breach Coverage', 'Accounts Receivable', 'Valuable Papers'],
  },
  {
    title: 'Specialized & High-Risk',
    icon: 'ri-tools-line',
    image: 'https://readdy.ai/api/search-image?query=coal%20mining%20heavy%20equipment%20Appalachian%20open%20pit%20mine%20dump%20trucks%20excavators%20industrial%20landscape%20dramatic%20photography%20wide%20angle%20powerful%20machinery&width=700&height=460&seq=svc2026e&orientation=landscape',
    desc: 'Custom-tailored policies for coal mines, contractors, and high-risk operations that need more than standard coverage.',
    items: ['Coal Mine Insurance', 'Contractor Liability', 'Pollution Liability', 'Surety Bonds', 'Umbrella Policies', 'Professional Liability'],
  },
  {
    title: 'Personal Lines',
    icon: 'ri-home-heart-line',
    image: 'https://readdy.ai/api/search-image?query=cozy%20family%20home%20Appalachian%20countryside%20lush%20green%20trees%20mountains%20warm%20afternoon%20light%20welcoming%20front%20porch%20peaceful%20rural%20setting%20professional%20photography&width=700&height=460&seq=svc2026f&orientation=landscape',
    desc: 'Protecting families across the Tri-State area with home, auto, and life insurance tailored to your needs.',
    items: ['Homeowners Insurance', 'Auto Insurance', 'Life Insurance', 'Renters Insurance', 'Umbrella Policies', 'Flood Insurance'],
  },
];

export default function Services() {
  useEffect(() => {
    document.title = 'Business Insurance Services KY TN VA | Summit Appalachia Insurance';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${import.meta.env.VITE_SITE_URL}/services#webpage`,
      url: `${import.meta.env.VITE_SITE_URL}/services`,
      name: 'Business Insurance Services KY TN VA | Summit Appalachia Insurance',
      description: 'Commercial insurance services including general liability, workers compensation, commercial auto, BOP, and specialized high-risk coverage for Appalachian businesses in KY, TN, VA, and WV.',
      isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL}/#website` },
      about: {
        '@type': 'InsuranceAgency',
        name: 'Summit Appalachia Insurance',
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Business Insurance Services',
          itemListElement: services.map((svc, i) => ({
            '@type': 'Offer',
            position: i + 1,
            itemOffered: {
              '@type': 'Service',
              name: svc.title,
              description: svc.desc,
            },
          })),
        },
      },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-services';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-services')?.remove(); };
  }, []);

  return (
    <div className="font-sans bg-[#0a0a0a]">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://readdy.ai/api/search-image?query=heavy%20industrial%20equipment%20Appalachian%20construction%20site%20dramatic%20wide%20angle%20photography%20powerful%20machinery%20dark%20moody%20tones%20cinematic%20industrial%20landscape&width=1920&height=500&seq=svchero2026&orientation=landscape"
            alt="Services" className="w-full h-full object-cover object-top opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/50 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-16 w-full">
          <p className="text-white/30 text-xs font-sans mb-3 uppercase tracking-widest">
            <Link to="/" className="hover:text-[#c9a84c] cursor-pointer">Home</Link> / Services
          </p>
          <h1 className="font-serif text-5xl md:text-7xl text-white uppercase">Our Services</h1>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 bg-[#111111] border-b border-[#c9a84c]/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="text-[#c9a84c] text-xs font-bold uppercase tracking-[0.3em] font-sans">Comprehensive Coverage</span>
          <h2 className="font-serif text-4xl text-white mt-3 mb-4 uppercase">We Specialize In</h2>
          <p className="text-white/50 font-sans leading-relaxed">
            Commercial Property, General Liability, Workers&apos; Compensation, Commercial Auto, Business Owner Policies &quot;BOP&quot; and Personal Lines — all tailored for Appalachian businesses and families.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((svc, i) => (
              <div key={i} className="bg-[#111111] border border-[#c9a84c]/15 rounded-md overflow-hidden hover:border-[#c9a84c]/40 transition-colors group">
                <div className="w-full h-52 overflow-hidden">
                  <img src={svc.image} alt={svc.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500 opacity-80" />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#c9a84c]/10 rounded-md">
                      <i className={`${svc.icon} text-[#c9a84c] text-xl`} />
                    </div>
                    <h3 className="font-serif text-xl text-[#c9a84c] font-bold uppercase">{svc.title}</h3>
                  </div>
                  <p className="text-white/50 font-sans text-sm leading-relaxed mb-5">{svc.desc}</p>
                  <ul className="grid grid-cols-2 gap-2 mb-6">
                    {svc.items.map(item => (
                      <li key={item} className="flex items-center gap-2 text-xs text-white/50 font-sans">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-checkbox-circle-line text-[#c9a84c] text-sm" />
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" className="inline-block px-6 py-2.5 bg-[#c9a84c] text-black text-xs font-bold uppercase tracking-widest rounded-md hover:bg-[#e2c46e] transition-colors whitespace-nowrap cursor-pointer font-sans">
                    Get a Quote
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#c9a84c]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl text-black uppercase mb-4">Not Sure What You Need?</h2>
          <p className="text-black/70 font-sans mb-8">Call us today and we&apos;ll walk you through your options. No pressure, just honest advice.</p>
          <a href="tel:6062691190" className="inline-block px-8 py-4 bg-black text-[#c9a84c] font-bold text-sm uppercase tracking-widest rounded-md hover:bg-[#111] transition-colors whitespace-nowrap cursor-pointer font-sans">
            Call 606-269-1190
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
